import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import useDebounced from '../../../hooks/use-debounced';
import {applySupersedes, groupCommandSearchResults, mergeCommandSearchResults} from './merge-results';
import {useCommandSearchKeyboard} from './use-command-shortcut';
import {CommandSearchRegistry} from '../registry/registry';
import {createGlobalScope} from '../scope/keys';
import {replayRecentAction} from '../recent/recent-handlers';
import {
    clearRecentResults,
    readRecentResults,
    removeRecentResult,
    toStoredRecentResult,
    writeRecentResult,
} from '../recent/recent-store';
import type {CommandSearchGroup, CommandSearchNavigate, CommandSearchResult, UseCommandSearchOptions} from '../types';
import {scoreCommandSearchFields} from '../match-query';

const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

/**
 * Orchestrates command-search state, provider execution, and keyboard selection.
 */
export const useCommandSearch = (options: UseCommandSearchOptions) => {
    const history = useHistory();
    const navigate = useCallback<CommandSearchNavigate>((to) => {
        history.push(to);
    }, [history]);
    const panelId = options.panelId ?? 'main';
    const recentOptions = useMemo(() => ({
        storageKey: options.storageKey,
        maxItems: options.maxRecentItems,
    }), [options.maxRecentItems, options.storageKey]);

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [, , debouncedQuery] = useDebounced(query, options.searchDebounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS);
    const [results, setResults] = useState<CommandSearchResult[]>([]);
    const [loadingCategories, setLoadingCategories] = useState<Set<string>>(new Set());
    const [activeIndex, setActiveIndex] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    /**
     * Manual expand/collapse overrides keyed by category.
     * Cleared when the query changes or the dialog closes so defaults re-apply.
     */
    const [expandOverrides, setExpandOverrides] = useState<Map<string, boolean>>(new Map());
    /**
     * Categories auto-expanded for the current query (via the small-result-set rule).
     * Sticky so late-arriving async results cannot collapse already-open groups.
     * Cleared when the query changes or the dialog closes.
     */
    const [stickyAutoExpanded, setStickyAutoExpanded] = useState<Set<string>>(new Set());
    /** Bumped when recents are removed/cleared so the search effect re-reads session storage. */
    const [recentRevision, setRecentRevision] = useState(0);

    const requestIdRef = useRef(0);
    const isQueryPending = query !== debouncedQuery;

    const openSearch = useCallback(() => {
        setOpen(true);
        setActiveIndex(0);
    }, []);

    const closeSearch = useCallback(() => {
        setOpen(false);
        setQuery('');
        setActiveIndex(0);
        setSelectedCategory(null);
        setExpandOverrides(new Map());
        setStickyAutoExpanded(new Set());
    }, []);

    /** Reset category chip filter and expand state whenever the user changes the search text. */
    useEffect(() => {
        setSelectedCategory(null);
        setExpandOverrides(new Map());
        setStickyAutoExpanded(new Set());
    }, [query]);

    useCommandSearchKeyboard({open, onOpen: openSearch, onClose: closeSearch});

    useEffect(() => {
        if (!open) {
            return;
        }

        CommandSearchRegistry.setScope(CommandSearchRegistry.getScope() ?? createGlobalScope(panelId));

        const abortController = new AbortController();
        const activeProviders = CommandSearchRegistry.getActiveProviders(
            CommandSearchRegistry.buildContext('', undefined, navigate, abortController, options.services),
        );

        void Promise.allSettled(
            activeProviders
                .filter((provider) => provider.prefetch)
                .map((provider) => {
                    const ctx = CommandSearchRegistry.buildContext(
                        '',
                        undefined,
                        navigate,
                        abortController,
                        options.services,
                    );
                    return provider.prefetch!(ctx);
                }),
        );

        return () => abortController.abort();
    }, [navigate, open, options.services, panelId]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const requestId = ++requestIdRef.current;
        const abortController = new AbortController();
        const trimmedQuery = debouncedQuery.trim();
        const scope = CommandSearchRegistry.getScope() ?? createGlobalScope(panelId);
        const ctx = CommandSearchRegistry.buildContext(
            trimmedQuery,
            scope,
            navigate,
            abortController,
            options.services,
        );

        const recentResults: CommandSearchResult[] = readRecentResults(recentOptions)
            .filter((entry) => !trimmedQuery || entry.title.toLowerCase().includes(trimmedQuery.toLowerCase()))
            .map((entry) => {
                const relevance = trimmedQuery
                    ? scoreCommandSearchFields(trimmedQuery, {
                        title: entry.title,
                        subtitle: entry.meta,
                        category: 'recent',
                        type: 'Recent',
                    })
                    : 1;
                return {
                    id: `recent-${entry.id}`,
                    category: 'recent',
                    title: entry.title,
                    badge: 'Recent',
                    badgeColor: 'default' as const,
                    meta: entry.meta,
                    avatarUrl: entry.avatarUrl,
                    icon: options.resolveResultIcon?.(entry.id),
                    // Modest sticky boost so recents stay visible without beating exact current matches.
                    score: relevance > 0 ? relevance + 8 : 0,
                    ...(entry.actionKey === 'navigate' && typeof entry.actionPayload?.to === 'string'
                        ? {to: entry.actionPayload.to}
                        : {}),
                    ...(entry.actionKey === 'tutorial' && typeof entry.actionPayload?.href === 'string'
                        ? {
                            to: entry.actionPayload.href,
                            target: '_blank',
                            rel: 'noopener noreferrer',
                        }
                        : {}),
                    onSelect: () => replayRecentAction(entry.actionKey, entry.actionPayload, ctx),
                };
            })
            .filter((entry) => (entry.score ?? 0) > 0);

        const activeProviders = CommandSearchRegistry.getActiveProviders(ctx);
        const activeProviderIds = new Set(activeProviders.map((provider) => provider.id));
        const supersededBy = CommandSearchRegistry.getSupersedesMap(activeProviders);

        const providerResults = activeProviders.map((provider) => ({
            provider,
            result: provider.search(ctx),
        }));

        const syncResults = providerResults.filter(({result}) => !(result instanceof Promise));
        const asyncResults = providerResults.filter(({result}) => result instanceof Promise);

        const buckets: Partial<Record<string, CommandSearchResult[]>> = {
            recent: recentResults,
        };

        /** Distributes results into buckets keyed by each item's category (supports multi-category providers). */
        const appendItemsToBuckets = (items: CommandSearchResult[]) => {
            items.forEach((item) => {
                const category = item.category;
                buckets[category] = [...(buckets[category] ?? []), item];
            });
        };

        /** Clears previously appended items for a provider before writing fresh results. */
        const clearProviderFromBuckets = (providerId: string) => {
            Object.keys(buckets).forEach((key) => {
                buckets[key] = (buckets[key] ?? []).filter((item) => item.providerId !== providerId);
            });
        };

        syncResults.forEach(({provider, result}) => {
            const items = result as CommandSearchResult[];
            const tagged = items.map((item) => ({...item, providerId: provider.id}));
            appendItemsToBuckets(tagged);
        });

        const mergedSync = applySupersedes(
            mergeCommandSearchResults(buckets, options.categoryOrder),
            activeProviderIds,
            supersededBy,
        );

        if (requestId === requestIdRef.current) {
            setResults(mergedSync);
            setActiveIndex(0);
        }

        if (!asyncResults.length) {
            setLoadingCategories(new Set());
            return () => abortController.abort();
        }

        const pending = new Set<string>();
        asyncResults.forEach(({provider}) => {
            pending.add(provider.category);
            // Nexus suggestions span per-industry categories — mark those as loading too.
            if (provider.category === 'nexus') {
                options.categoryOrder
                    .filter((category) => category === 'nexus' || category.startsWith('nexus-'))
                    .forEach((category) => pending.add(category));
            }
        });
        setLoadingCategories(new Set(pending));

        const clearProviderPending = (providerCategory: string) => {
            pending.delete(providerCategory);
            if (providerCategory === 'nexus') {
                options.categoryOrder
                    .filter((category) => category === 'nexus' || category.startsWith('nexus-'))
                    .forEach((category) => pending.delete(category));
            }
        };

        const finishProvider = (providerId: string, providerCategory: string, items: CommandSearchResult[]) => {
            if (requestId !== requestIdRef.current) {
                return;
            }
            clearProviderFromBuckets(providerId);
            appendItemsToBuckets(items);
            clearProviderPending(providerCategory);
            setLoadingCategories(new Set(pending));
            const merged = applySupersedes(
                mergeCommandSearchResults(buckets, options.categoryOrder),
                activeProviderIds,
                supersededBy,
            );
            setResults(merged);
        };

        asyncResults.forEach(({provider, result}) => {
            void Promise.resolve(result)
                .then((items) => {
                    const tagged = items.map((item) => ({...item, providerId: provider.id}));
                    finishProvider(provider.id, provider.category, tagged);
                })
                .catch(() => finishProvider(provider.id, provider.category, []));
        });

        return () => abortController.abort();
    }, [
        debouncedQuery,
        navigate,
        open,
        options.categoryOrder,
        options.resolveResultIcon,
        options.services,
        panelId,
        recentOptions,
        recentRevision,
    ]);

    const allGroups = useMemo(
        () => groupCommandSearchResults(
            results,
            loadingCategories,
            options.categoryOrder,
            options.categoryLabels,
            {sortByRelevance: !!debouncedQuery.trim()},
        ),
        [debouncedQuery, loadingCategories, options.categoryLabels, options.categoryOrder, results],
    );

    /** Drop a stale chip selection when that category disappears from results. */
    useEffect(() => {
        if (!selectedCategory) {
            return;
        }
        if (!allGroups.some((group) => group.category === selectedCategory)) {
            setSelectedCategory(null);
        }
    }, [allGroups, selectedCategory]);

    const groups = useMemo(
        () => (selectedCategory
            ? allGroups.filter((group) => group.category === selectedCategory)
            : allGroups),
        [allGroups, selectedCategory],
    );

    /**
     * Accumulate sticky auto-expansion:
     * - once the visible result set is small (<10), remember those groups
     * - once only a single non-recent category has hits, remember that group
     * so later async results cannot collapse them for this query.
     */
    useEffect(() => {
        const totalItems = groups.reduce((sum, group) => sum + group.items.length, 0);
        const contentGroups = groups.filter((group) => group.category !== 'recent' && group.items.length > 0);
        const soleContentCategory = contentGroups.length === 1 ? contentGroups[0].category : null;

        if ((totalItems <= 0 || totalItems >= 10) && !soleContentCategory) {
            return;
        }

        setStickyAutoExpanded((prev) => {
            let changed = false;
            const next = new Set(prev);

            if (totalItems > 0 && totalItems < 10) {
                groups.forEach((group) => {
                    if (!next.has(group.category)) {
                        next.add(group.category);
                        changed = true;
                    }
                });
            }

            if (soleContentCategory && !next.has(soleContentCategory)) {
                next.add(soleContentCategory);
                changed = true;
            }

            return changed ? next : prev;
        });
    }, [groups]);

    /**
     * Default expansion:
     * - `recent` is always open when present
     * - all groups when total items < 10
     * - the sole non-recent category when it is the only content group (even if ≥10)
     * - groups auto-expanded while the result set was small / sole stay open (sticky)
     * - category chip selection also forces that group open
     */
    const defaultExpandedCategories = useMemo(() => {
        const next = new Set<string>();
        const totalItems = groups.reduce((sum, group) => sum + group.items.length, 0);
        const contentGroups = groups.filter((group) => group.category !== 'recent' && group.items.length > 0);

        if (groups.some((group) => group.category === 'recent')) {
            next.add('recent');
        }

        // Live small-result rule (applied immediately to avoid a first-render flash).
        if (totalItems > 0 && totalItems < 10) {
            groups.forEach((group) => next.add(group.category));
        }

        // Sole non-recent category stays open even when there are many hits.
        if (contentGroups.length === 1) {
            next.add(contentGroups[0].category);
        }

        // Sticky auto-expanded groups (only those still present in current results).
        groups.forEach((group) => {
            if (stickyAutoExpanded.has(group.category)) {
                next.add(group.category);
            }
        });

        if (selectedCategory) {
            next.add(selectedCategory);
        }

        return next;
    }, [groups, selectedCategory, stickyAutoExpanded]);

    const expandedCategories = useMemo(() => {
        const next = new Set(defaultExpandedCategories);
        expandOverrides.forEach((expanded, category) => {
            if (expanded) {
                next.add(category);
            } else {
                next.delete(category);
            }
        });
        // Chip filter always wins: the filtered group stays expanded even if manually collapsed.
        if (selectedCategory) {
            next.add(selectedCategory);
        }
        return next;
    }, [defaultExpandedCategories, expandOverrides, selectedCategory]);

    /** Keyboard / select targets only include items from expanded groups. */
    const flatResults = useMemo(
        () => groups
            .filter((group) => expandedCategories.has(group.category))
            .flatMap((group) => group.items),
        [expandedCategories, groups],
    );

    const toggleCategory = useCallback((category: string) => {
        setSelectedCategory((prev) => (prev === category ? null : category));
        setActiveIndex(0);
    }, []);

    const toggleGroupExpanded = useCallback((category: string) => {
        setExpandOverrides((prev) => {
            const currentlyExpanded = prev.has(category)
                ? prev.get(category)!
                : defaultExpandedCategories.has(category);
            const next = new Map(prev);
            next.set(category, !currentlyExpanded);
            return next;
        });
        setActiveIndex(0);
    }, [defaultExpandedCategories]);

    useEffect(() => {
        if (activeIndex >= flatResults.length) {
            setActiveIndex(Math.max(0, flatResults.length - 1));
        }
    }, [activeIndex, flatResults.length]);

    const removeRecent = useCallback((resultId: string) => {
        const storedId = resultId.startsWith('recent-')
            ? resultId.slice('recent-'.length)
            : resultId;
        if (!storedId) {
            return;
        }
        removeRecentResult(recentOptions, storedId);
        setRecentRevision((prev) => prev + 1);
        setActiveIndex(0);
    }, [recentOptions]);

    const clearRecents = useCallback(() => {
        clearRecentResults(recentOptions);
        setRecentRevision((prev) => prev + 1);
        setActiveIndex(0);
    }, [recentOptions]);

    /**
     * Persists a result to Recents without navigating or closing the palette.
     * Optional actionKey/actionPayload override the result's recentMeta (e.g. Edit vs View).
     */
    const rememberResult = useCallback((
        result: CommandSearchResult,
        actionKey?: string,
        actionPayload?: Record<string, string | number | boolean | undefined>,
    ) => {
        if (actionKey) {
            writeRecentResult(recentOptions, toStoredRecentResult(
                result,
                actionKey,
                actionPayload ?? {},
            ));
            setRecentRevision((prev) => prev + 1);
            return;
        }

        if (result.recentMeta) {
            writeRecentResult(recentOptions, toStoredRecentResult(
                result,
                result.recentMeta.actionKey,
                result.recentMeta.actionPayload ?? {},
            ));
            setRecentRevision((prev) => prev + 1);
            return;
        }

        if (result.category !== 'recent') {
            writeRecentResult(recentOptions, toStoredRecentResult(result, result.category, {id: result.id}));
            setRecentRevision((prev) => prev + 1);
        }
    }, [recentOptions]);

    const selectResult = useCallback(async (result?: CommandSearchResult) => {
        if (!result) {
            return;
        }

        const selectionResult = await Promise.resolve(result.onSelect());
        if (selectionResult === false) {
            return;
        }

        rememberResult(result);
        closeSearch();
    }, [closeSearch, rememberResult]);

    const moveActiveIndex = useCallback((delta: number) => {
        if (!flatResults.length) {
            return;
        }
        setActiveIndex((prev) => {
            const next = prev + delta;
            if (next < 0) {
                return flatResults.length - 1;
            }
            if (next >= flatResults.length) {
                return 0;
            }
            return next;
        });
    }, [flatResults.length]);

    const onDialogKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            moveActiveIndex(1);
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            moveActiveIndex(-1);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            selectResult(flatResults[activeIndex]);
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            closeSearch();
        }
    }, [activeIndex, closeSearch, flatResults, moveActiveIndex, selectResult]);

    return {
        open,
        query,
        setQuery,
        openSearch,
        closeSearch,
        /** Groups after category-chip filtering (used for the results list). */
        groups,
        /** Unfiltered groups for the category chip row (includes loading). */
        allGroups,
        flatResults,
        activeIndex,
        setActiveIndex,
        selectedCategory,
        toggleCategory,
        expandedCategories,
        toggleGroupExpanded,
        selectResult,
        rememberResult,
        removeRecent,
        clearRecents,
        onDialogKeyDown,
        loadingCategories,
        hasQuery: !!query.trim(),
        isSearching: isQueryPending || loadingCategories.size > 0,
    };
};

export type {CommandSearchGroup, CommandSearchResult, UseCommandSearchOptions};
